import type { ProductCategorySummary } from '@eliadmin/core/src/api/products/types/product-types';
import type { FitmentProduct } from '../../api/product-fitments-api';

export const FITMENT_CATEGORY_FILTER_LIMIT = 100;
// /store/categories?childs=true defaults to ten descendant levels (root depth is zero).
export const FITMENT_CATEGORY_TREE_DEPTH_LIMIT = 10;

export type FitmentCategoryNode = {
  id: number;
  category: ProductCategorySummary;
  parentId: number | null;
  ancestorIds: number[];
  childIds: number[];
  subtreeIds: number[];
  depth: number;
  complete: boolean;
};

export type FitmentCategoryIndex = {
  byId: Map<number, FitmentCategoryNode>;
  rootIds: number[];
  available: boolean;
  complete: boolean;
};

export type FitmentCategorySelectionFilter =
  | { status: 'ready'; categoryIds: number[] }
  | {
      status: 'too-many';
      categoryIds: [];
      requiredCount: number;
    };

export type FitmentCategoryOption = {
  id: number;
  label: string;
  category: ProductCategorySummary;
};

export type FitmentCategoryGroup = {
  key: string;
  categoryId: number | null;
  name: string;
  category: ProductCategorySummary | null;
  products: FitmentProduct[];
};

export function normalizeFitmentCategoryId(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

type CategoryRecord = {
  category: ProductCategorySummary;
  parentIds: Set<number | null>;
  childIds: Set<number>;
  structureComplete: boolean;
  invalid: boolean;
};

const numericSort = (a: number, b: number) => a - b;

/** Index the supplied tree only. Missing child data is not evidence of a leaf. */
export function buildFitmentCategoryIndex(
  categories?: readonly ProductCategorySummary[] | null,
): FitmentCategoryIndex {
  const available = Array.isArray(categories);
  const records = new Map<number, CategoryRecord>();
  let invalidRoot = false;

  const visit = (
    category: ProductCategorySummary,
    parentId: number | null,
    path: Set<number>,
  ): number | null => {
    const id = normalizeFitmentCategoryId(category?.id);
    if (id === null) {
      if (parentId === null) invalidRoot = true;
      return null;
    }
    if (path.has(id)) {
      const previous = records.get(id);
      if (previous) previous.invalid = true;
      return null;
    }

    const children = Array.isArray(category.childs) ? category.childs : [];
    const structureComplete =
      category.has_childs === false
        ? children.length === 0
        : Array.isArray(category.childs) && children.length > 0;
    const record = records.get(id) ?? {
      category: { ...category, id },
      parentIds: new Set<number | null>(),
      childIds: new Set<number>(),
      structureComplete: false,
      invalid: false,
    };
    // Duplicate records with the same ID remain one node; choose its label deterministically.
    if (
      `${category.name}\u0000${category.slug}` <
      `${record.category.name}\u0000${record.category.slug}`
    ) {
      record.category = { ...category, id };
    }
    record.parentIds.add(parentId);
    record.structureComplete ||= structureComplete;
    record.invalid ||= category.has_childs === false && children.length > 0;
    // The backend sets loaded children=[] at its depth boundary and derives has_childs
    // from that truncated relation. Even has_childs=false cannot prove a leaf here.
    record.invalid ||= path.size >= FITMENT_CATEGORY_TREE_DEPTH_LIMIT;
    records.set(id, record);

    const nextPath = new Set(path).add(id);
    for (const child of children) {
      const childId = visit(child, id, nextPath);
      if (childId === null) record.invalid = true;
      else record.childIds.add(childId);
    }
    return id;
  };

  for (const category of Array.isArray(categories) ? categories : []) {
    visit(category, null, new Set());
  }

  const parentFor = (record: CategoryRecord) =>
    record.parentIds.has(null)
      ? null
      : ([...record.parentIds].filter((id): id is number => id !== null).sort(numericSort)[0] ??
        null);

  const byId = new Map<number, FitmentCategoryNode>();
  for (const [id, record] of records) {
    const ancestorIds: number[] = [];
    const seenAncestors = new Set([id]);
    let parentId = parentFor(record);
    let complete = true;
    while (parentId !== null) {
      if (seenAncestors.has(parentId)) {
        complete = false;
        break;
      }
      seenAncestors.add(parentId);
      ancestorIds.unshift(parentId);
      const parent = records.get(parentId);
      if (!parent) {
        complete = false;
        break;
      }
      parentId = parentFor(parent);
    }

    const descendants = new Set<number>();
    const walkSubtree = (childId: number, path: Set<number>) => {
      if (path.has(childId)) {
        complete = false;
        return;
      }
      if (descendants.has(childId)) return;
      descendants.add(childId);
      const child = records.get(childId);
      if (!child) {
        complete = false;
        return;
      }
      if (!child.structureComplete || child.invalid || child.parentIds.size > 1) complete = false;
      const nextPath = new Set(path).add(childId);
      for (const descendantId of child.childIds) walkSubtree(descendantId, nextPath);
    };
    walkSubtree(id, new Set());

    byId.set(id, {
      id,
      category: record.category,
      parentId: parentFor(record),
      ancestorIds,
      childIds: [...record.childIds].sort(numericSort),
      subtreeIds: [id, ...[...descendants].filter(childId => childId !== id).sort(numericSort)],
      depth: ancestorIds.length,
      complete,
    });
  }

  return {
    byId,
    rootIds: [...byId.values()]
      .filter(node => node.parentId === null)
      .map(node => node.id)
      .sort(numericSort),
    available,
    complete: available && !invalidRoot && [...byId.values()].every(node => node.complete),
  };
}

/**
 * RS Moto's Elastic sources search indexes ancestors, so one parent ID selects its
 * entire branch. Keep the user's explicit checkbox choices separate from these
 * minimal OR-filter IDs; never enumerate/truncate descendants or fetch all products.
 * The backend's non-Elastic exact-ID fallback does not have this parent semantics.
 */
export function resolveFitmentCategorySelection(
  categoryIds: readonly number[],
  index: FitmentCategoryIndex,
): FitmentCategorySelectionFilter {
  const selectedIds = new Set(
    categoryIds.map(normalizeFitmentCategoryId).filter((id): id is number => id !== null),
  );
  const effectiveIds = [...selectedIds]
    .filter(id => {
      const node = index.byId.get(id);
      return !node?.ancestorIds.some(
        ancestorId =>
          selectedIds.has(ancestorId) &&
          (index.byId.get(ancestorId)?.depth ?? Infinity) < node.depth,
      );
    })
    .sort(numericSort);
  if (effectiveIds.length > FITMENT_CATEGORY_FILTER_LIMIT) {
    return { status: 'too-many', categoryIds: [], requiredCount: effectiveIds.length };
  }
  return { status: 'ready', categoryIds: effectiveIds };
}

/** All shop categories, independent of which products happen to be on the current page. */
export function buildFitmentCategoryOptions(index: FitmentCategoryIndex): FitmentCategoryOption[] {
  const options: FitmentCategoryOption[] = [];
  const visited = new Set<number>();
  const nameFor = (id: number) => index.byId.get(id)?.category.name?.trim() || `Kategória #${id}`;
  const sortIds = (ids: number[]) =>
    [...ids].sort((a, b) => nameFor(a).localeCompare(nameFor(b), 'sk') || a - b);

  const visit = (id: number) => {
    if (visited.has(id)) return;
    visited.add(id);
    const node = index.byId.get(id);
    if (!node) return;

    options.push({
      id,
      label: [...node.ancestorIds, id].map(nameFor).join(' / '),
      category: node.category,
    });
    for (const childId of sortIds(node.childIds)) visit(childId);
  };

  for (const id of sortIds(index.rootIds)) visit(id);

  // Distinct imported categories can even have the same full path.
  const labelCounts = new Map<string, number>();
  for (const option of options) {
    labelCounts.set(option.label, (labelCounts.get(option.label) ?? 0) + 1);
  }
  return options.map(option =>
    labelCounts.get(option.label)! > 1
      ? { ...option, label: `${option.label} (#${option.id})` }
      : option,
  );
}

/** Search keeps matching branches and their ancestors; expansion still controls visibility. */
export function getVisibleFitmentCategoryOptions(
  options: readonly FitmentCategoryOption[],
  index: FitmentCategoryIndex,
  expandedIds: ReadonlySet<number>,
  search = '',
): FitmentCategoryOption[] {
  const normalize = (text: string) =>
    text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('sk');
  const terms = normalize(search).trim().split(/\s+/).filter(Boolean);
  const matchingIds = new Set<number>();
  if (terms.length > 0) {
    for (const option of options) {
      const label = normalize(option.label);
      if (!terms.every(term => label.includes(term))) continue;
      matchingIds.add(option.id);
      for (const ancestorId of index.byId.get(option.id)?.ancestorIds ?? []) {
        matchingIds.add(ancestorId);
      }
    }
  }
  return options.filter(option => {
    if (terms.length > 0 && !matchingIds.has(option.id)) return false;
    return index.byId.get(option.id)?.ancestorIds.every(id => expandedIds.has(id)) ?? false;
  });
}

/** Group this page, not the entire result set. Keep server order inside each group. */
export function groupFitmentProductsByCategory(
  products: readonly FitmentProduct[],
  index: FitmentCategoryIndex,
  selectedCategoryIds?: number | string | null | readonly (number | string)[],
): FitmentCategoryGroup[] {
  const selectedIds = (
    Array.isArray(selectedCategoryIds) ? selectedCategoryIds : [selectedCategoryIds]
  )
    .map(normalizeFitmentCategoryId)
    .filter((id): id is number => id !== null);
  const preferredIds = new Set(selectedIds.flatMap(id => index.byId.get(id)?.subtreeIds ?? [id]));
  const groups = new Map<string, FitmentCategoryGroup>();
  const seenProducts = new Set<number>();

  for (const product of products) {
    if (seenProducts.has(product.id)) continue;
    seenProducts.add(product.id);

    const assigned = new Map<number, ProductCategorySummary>();
    for (const category of product.categories ?? []) {
      const id = normalizeFitmentCategoryId(category?.id);
      if (id === null) continue;
      const resolved = index.byId.get(id)?.category ?? category;
      if (!resolved.name?.trim()) continue;
      const previous = assigned.get(id);
      if (!previous || resolved.name < previous.name) assigned.set(id, resolved);
    }
    const categoryId = [...assigned.keys()].sort(
      (a, b) =>
        Number(preferredIds.has(b)) - Number(preferredIds.has(a)) ||
        (index.byId.get(b)?.depth ?? 0) - (index.byId.get(a)?.depth ?? 0) ||
        a - b,
    )[0];
    const category = categoryId === undefined ? null : assigned.get(categoryId)!;
    const key = category ? `category-${categoryId}` : 'uncategorized';
    const name = category?.name.trim() || 'Ostatné doplnky';
    const existing = groups.get(key);
    if (existing) {
      existing.products.push(product);
      if (name < existing.name) {
        existing.name = name;
        existing.category = category;
      }
    } else {
      groups.set(key, { key, categoryId: categoryId ?? null, name, category, products: [product] });
    }
  }

  return [...groups.values()].sort((a, b) => {
    if (a.categoryId === null) return b.categoryId === null ? 0 : 1;
    if (b.categoryId === null) return -1;
    return a.name.localeCompare(b.name, 'sk') || a.categoryId - b.categoryId;
  });
}
